import { useState, useEffect } from "react";
import CampoCheck from "../../Campos/CampoCheck";
import { getEnderecoCadastro } from "../../../api/Cadastro";
import CampoSimNao from "../../Campos/CampoSimNao";

import { getUFs } from "../../../api/Endereco";
import useAlert from "../../../hooks/useAlert";
import useStyle from "./styles";
import { Skeleton, Box } from "@mui/material";
import Subtitulo from "../../Textos/Subtitulo";
import EnderecoNacional from "./EnderecoNacional";
import EnderecoExterior from "./EnderecoExterior";

export default function EnderecoPrincipal({
  user,
  setData,
  isLoading,
  enderecoDefaultValue = null,
  habilitarEnderecoExterior = false,
  // Novas props genéricas para validação
  validationErrors = {}, // { cep: 'mensagem', logradouro: 'mensagem' }
  hasValidationError, // função (campo) => boolean
  entityType = "cliente", // "cliente", "socio", etc.
  entityIndex = 0, // índice se for array (como sócios)
}) {
  const { showAlertError } = useAlert();

  const [enderecoState, setEnderecoState] = useState({
    bairro: enderecoDefaultValue?.bairro ?? "",
    cep: enderecoDefaultValue?.cep ?? "",
    cidade: enderecoDefaultValue?.cidade ?? "",
    complemento: enderecoDefaultValue?.complemento ?? "",
    logradouro: enderecoDefaultValue?.logradouro ?? "",
    numero: enderecoDefaultValue?.numero ?? "",
    idCliente: user !== null ? user.id : 0,
    idPais: enderecoDefaultValue?.idPais ?? 0,
    idUf: enderecoDefaultValue?.idUf ?? 0,
    ufDescricao: enderecoDefaultValue?.ufDescricao ?? "",
    id: enderecoDefaultValue?.id ?? 0,
    paises: enderecoDefaultValue?.paises ?? [],
    domiciliadoExterior: enderecoDefaultValue?.domiciliadoExterior ?? false,
    ufs: enderecoDefaultValue?.ufs ?? [],
  });

  const [cepChanged, setCepChanged] = useState(false);
  const [isLoadingUfs, setIsLoadingUfs] = useState(true);

  const classes = useStyle();

  // Função para carregar as UFs
  const carregarUFs = async () => {
    try {
      setIsLoadingUfs(true);
      const response = await getUFs();

      if (response.errorMessage && response.errorMessage.length > 0) {
        throw new Error(response.errorMessage);
      }

      const ufsData = response.data || [];
      return ufsData;
    } catch (error) {
      showAlertError("Erro ao carregar lista de UFs: " + error.message);
      return [];
    } finally {
      setIsLoadingUfs(false);
    }
  };

  const init = async () => {
    try {
      // Carrega as UFs primeiro
      const ufsCarregadas = await carregarUFs();

      if (user) {
        const response = await getEnderecoCadastro(user.id);

        if (response.errorMessage && response.errorMessage.length > 0) {
          throw new Error(response.errorMessage);
        }

        let enderecoNovo;

        if (response.data && response.data.id > 0) {
          // Cliente EXISTE - monta o endereço com dados do cliente + UFs
          enderecoNovo = {
            bairro: response.data.bairro ?? "",
            cep: response.data.cep ?? "",
            cidade: response.data.cidade ?? "",
            complemento: response.data.complemento ?? "",
            logradouro: response.data.logradouro ?? "",
            numero: response.data.numero ?? "",
            idCliente: user.id,
            idPais: response.data.idPais ?? 0,
            idUf: response.data.idUf ?? 0, // Este deve vir preenchido
            ufDescricao: response.data.ufDescricao ?? "",
            id: response.data.id ?? 0,
            paises: response.data.paises ?? [],
            domiciliadoExterior: response.data.domiciliadoExterior ?? false,
            ufs: ufsCarregadas, // IMPORTANTE: inclui as UFs aqui
          };
        } else if (response.data) {
          // Cliente NÃO existe mas tem dados básicos
          enderecoNovo = {
            ...enderecoState,
            paises: response.data.paises ?? [],
            ufs: ufsCarregadas, // Inclui as UFs
          };
        } else {
          // Caso não tenha response.data
          enderecoNovo = {
            ...enderecoState,
            ufs: ufsCarregadas, // Inclui as UFs
          };
        }

        setEnderecoState(enderecoNovo);
        setData(enderecoNovo);
      } else {
        // Se não tem user
        let enderecoNovo = {
          ...enderecoState,
          ufs: ufsCarregadas,
        };
        setEnderecoState(enderecoNovo);
        setData(enderecoNovo);
      }
    } catch (error) {
      showAlertError(error.message);
    }
  };

  useEffect(() => {
    init();
  }, []);

  const handlerChangeEndereco = (enderecoNew) => {
    // Mantém as UFs no novo endereço
    const enderecoComUfs = {
      ...enderecoNew,
      ufs: enderecoNew.ufs || enderecoState.ufs,
    };
    setEnderecoState(enderecoComUfs);
    setData(enderecoComUfs);
  };

  const handleChangeCheck = (e) => {
    let enderecoNovo = {
      ...enderecoState,
      cep: "",
      cidade: "",
      bairro: "",
      logradouro: "",
      numero: "",
      complemento: "",
      idUf: 0,
      ufDescricao: "",
      idPais: 0,
      domiciliadoExterior: e.target.checked,
      // Mantém as UFs
    };

    setEnderecoState(enderecoNovo);
    setData(enderecoNovo);
  };

  const isLoadingCompleto = isLoading || isLoadingUfs;

  return (
    <>
      <div className={classes.box}>
        {isLoadingCompleto ? (
          <Skeleton animation="wave" height={40} width={"20%"} />
        ) : (
          <>
            {habilitarEnderecoExterior && (
              <div>
                <Subtitulo sx={{ mb: 2, fontSize: 15, color: "#000" }}>
                  Cadastre o endereço
                </Subtitulo>
                <Box sx={{ mb: 2 }}>
                  <CampoSimNao
                    key="check-endereco"
                    enable={true}
                    size="medium"
                    fullWidth={false}
                    descricao="Domiciliado Exterior"
                    name="domiciliadoExterior"
                    value={enderecoState.domiciliadoExterior}
                    handleChange={handleChangeCheck} // pode usar o mesmo handler
                    className={classes.campo}
                    required={false}
                  />
                  {/* <CampoCheck
                    key="check-endereco"
                    enable={true}
                    variant="filled"
                    size="medium"
                    sx={{ mb: 4 }}
                    fullWidth={false}
                    label="Domiciliado Exterior"
                    descricao=""
                    name="domiciliadoExterior"
                    value={enderecoState.domiciliadoExterior}
                    handleChangeCheck={handleChangeCheck}
                  /> */}
                </Box>
              </div>
            )}
          </>
        )}
        {habilitarEnderecoExterior && enderecoState.domiciliadoExterior ? (
          <EnderecoExterior
            enderecoDefaultValue={enderecoState}
            isLoading={isLoadingCompleto}
            setData={handlerChangeEndereco}
            // validationErrors={validationErrors}
            // hasValidationError={hasValidationError}
          />
        ) : (
          <EnderecoNacional
            enderecoDefaultValue={enderecoState}
            isLoading={isLoadingCompleto}
            setData={handlerChangeEndereco}
            cepSelected={cepChanged}
            onChangeCep={setCepChanged}
            validationErrors={validationErrors}
            hasValidationError={hasValidationError}
          />
        )}
      </div>
    </>
  );
}
